// Cloud control-plane DTOs (Phase 7): admin console, owner portal, node-facing endpoints. 04 / 12 §4 / 05 §3.3.
import { z } from 'zod';
import { dateSchema, uuidSchema } from './common.js';
// ---------------------------------------------------------------- license document (12 §4.1, ADR-0017)
export const licenseDocumentSchema = z.object({
    license_id: uuidSchema,
    schema_version: z.literal(1),
    tenant_id: uuidSchema,
    property_id: uuidSchema,
    node_public_key_fingerprint: z.string().regex(/^[0-9a-f]{32}$/),
    plan_code: z.string().min(1).max(32),
    room_cap: z.number().int().positive(),
    modules: z.array(z.string().min(1)).min(1),
    max_users: z.number().int().positive().nullable(),
    issued_at: z.iso.datetime(),
    valid_from: z.iso.datetime(),
    expires_at: z.iso.datetime(),
    grace_policy: z.object({
        warn_days: z.number().int().nonnegative(),
        degraded_days: z.number().int().nonnegative(),
    }),
    support_plan: z.enum(['basic', 'standard', 'priority']),
    release_channel: z.enum(['stable', 'staging']),
    issuer: z.string().min(1),
    revocation_check_url: z.string().min(1),
});
/** What travels to the node (also the offline paste/USB format): document + detached signature + key id. */
export const signedLicenseSchema = z.object({
    document: licenseDocumentSchema,
    signature: z.string().min(1),
    keyId: z.string().min(1),
});
// ---------------------------------------------------------------- admin console (staff)
export const staffLoginSchema = z.object({
    email: z.string().trim().email().max(128),
    password: z.string().min(1).max(256),
    /** TOTP or recovery code — required once the account is enrolled (09 §4.7: mandatory for staff and tenant admins) */
    code: z
        .string()
        .trim()
        .regex(/^(\d{6}|[a-z2-7]{4}-?[a-z2-7]{4})$/i)
        .optional(),
});
export const cloudMfaConfirmSchema = z.object({
    code: z
        .string()
        .trim()
        .regex(/^\d{6}$/),
});
export const tenantUserLoginSchema = staffLoginSchema;
export const tenantInputSchema = z.object({
    code: z
        .string()
        .trim()
        .regex(/^[A-Z0-9_-]{2,16}$/),
    nameAr: z.string().trim().min(2).max(120),
    nameEn: z.string().trim().min(2).max(120).optional(),
    contactEmail: z.string().email().optional(),
});
export const propertyInputSchema = z.object({
    code: z
        .string()
        .trim()
        .regex(/^[A-Z0-9_-]{2,16}$/),
    nameAr: z.string().trim().min(2).max(120),
    nameEn: z.string().trim().min(2).max(120).optional(),
    timezone: z.string().min(1).default('Asia/Damascus'),
    baseCurrency: z
        .string()
        .regex(/^[A-Z]{3}$/)
        .default('SYP'),
    roomCap: z.number().int().positive(),
    /** Phase 11: ministry view + wave kill switch */
    governorate: z.string().trim().min(2).max(64).optional(),
    category: z.string().trim().max(64).optional(),
    wave: z.number().int().min(1).max(9).optional(),
});
/** ADR-0023: the product is free and licenses are perpetual. `expires_at` stays a required field of the signed
 * document so N-1 nodes accept it unchanged; this sentinel keeps their degradation ladder at `ok` forever. */
export const PERPETUAL_EXPIRES_AT = '9999-12-31T23:59:59.000Z';
export const isPerpetualLicense = (expiresAt) => !!expiresAt && expiresAt.startsWith('9999-');
export const issueLicenseSchema = z.object({
    planCode: z.string().min(1).max(32).default('free'),
    roomCap: z.number().int().positive(),
    modules: z.array(z.string().min(1)).min(1).default(['pms_core', 'housekeeping', 'reports']),
    maxUsers: z.number().int().positive().nullable().default(null),
    validFrom: z.iso.datetime().optional(),
    // ponytail: kept overridable so ADR-0023's rollback (dated licenses) needs no code change
    expiresAt: z.iso.datetime().default(PERPETUAL_EXPIRES_AT),
    gracePolicy: z
        .object({
        warnDays: z.number().int().nonnegative(),
        degradedDays: z.number().int().nonnegative(),
    })
        .default({ warnDays: 30, degradedDays: 30 }),
    supportPlan: z.enum(['basic', 'standard', 'priority']).default('basic'),
    releaseChannel: z.enum(['stable', 'staging']).default('stable'),
    reason: z.string().max(500).optional(),
});
export const revokeLicenseSchema = z.object({ reason: z.string().trim().min(3).max(500) });
export const directoryUserInputSchema = z.object({
    username: z
        .string()
        .trim()
        .regex(/^[a-z0-9._-]{2,64}$/),
    displayNameAr: z.string().trim().min(2).max(120),
    displayNameEn: z.string().trim().max(120).optional(),
    password: z.string().min(10).max(256),
    assignments: z.array(z.object({ propertyId: uuidSchema, roleCode: z.string().min(1) })).min(1),
});
export const tenantUserInputSchema = z.object({
    email: z.string().trim().email().max(128),
    displayName: z.string().trim().min(2).max(120),
    password: z.string().min(12).max(256),
    role: z.enum(['tenant_admin', 'viewer']).default('tenant_admin'),
});
export const staffUserInputSchema = z.object({
    email: z.string().trim().email().max(128),
    displayName: z.string().trim().min(2).max(120),
    password: z.string().min(12).max(256),
    role: z.enum(['staff_admin', 'ops', 'support', 'sales']).default('ops'),
});
export const featureFlagsInputSchema = z.object({
    flags: z.record(z.string().regex(/^[a-z0-9_.]{2,64}$/), z.unknown()),
});
export const maintenanceWindowSchema = z.object({
    /** 0 = Sunday … 6 = Saturday (property local time) */
    dayOfWeek: z.number().int().min(0).max(6),
    startTime: z.string().regex(/^\d{2}:\d{2}$/),
    durationMinutes: z.number().int().min(15).max(480),
});
export const supportGrantRequestSchema = z.object({
    propertyId: uuidSchema,
    scope: z.enum(['readonly_ops', 'diagnostics', 'full']),
    reason: z.string().trim().min(5).max(500),
    ticket: z.string().trim().min(1).max(64),
    durationMinutes: z.number().int().min(15).max(240).default(60),
});
/** node → cloud while a readonly_ops grant is active (ADR-0020): health + recent node events, never PII */
export const supportSnapshotSchema = z.object({
    at: z.iso.datetime(),
    health: z.record(z.string(), z.unknown()),
    events: z
        .array(z.object({
        at: z.iso.datetime(),
        kind: z.string().max(64),
        level: z.enum(['info', 'warning', 'critical']),
        message: z.string().max(500),
    }))
        .max(100),
    config: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])),
});
// ---------------------------------------------------------------- tickets (P10-07) + onboarding (P10-13)
export const ticketInputSchema = z.object({
    tenantId: uuidSchema.optional(), // staff only; tenants are scoped by their session
    propertyId: uuidSchema.optional(),
    title: z.string().trim().min(3).max(160),
    body: z.string().trim().min(1).max(4000),
    severity: z.enum(['low', 'normal', 'high', 'critical']).default('normal'),
    grantId: uuidSchema.optional(),
});
export const ticketUpdateSchema = z.object({
    status: z.enum(['open', 'in_progress', 'waiting_hotel', 'resolved', 'closed']).optional(),
    assignedTo: uuidSchema.nullable().optional(),
});
export const ticketCommentSchema = z.object({ body: z.string().trim().min(1).max(4000) });
export const onboardingImportSchema = z.object({ csv: z.string().min(1).max(2_000_000) });
export const waveControlSchema = z.object({
    installsPaused: z.boolean(),
    reason: z.string().trim().max(500).optional(),
});
export const supportGrantDecisionSchema = z.object({
    decision: z.enum(['approve', 'reject']),
    note: z.string().max(500).optional(),
});
// ---------------------------------------------------------------- node-facing (05 §6, 09 §5.2)
export const activateNodeSchema = z.object({
    activationCode: z
        .string()
        .trim()
        .regex(/^[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}$/),
    publicKeyHex: z.string().regex(/^[0-9a-f]{64}$/),
    nodeVersion: z.string().min(1).max(32),
    hostname: z.string().max(128).optional(),
});
export const heartbeatSchema = z.object({
    nodeVersion: z.string().min(1).max(32),
    schemaVersion: z.number().int().nonnegative(),
    businessDate: dateSchema.nullable(),
    health: z.object({
        pgUp: z.boolean(),
        diskFreePct: z.number().nullable(),
        backupLastOkAt: z.iso.datetime().nullable(),
        backupLastDrillOk: z.boolean().nullable(),
        outboxPending: z.number().int().nonnegative(),
        powerLevel: z.string(),
        uncleanShutdowns7d: z.number().int().nonnegative(),
        alerts: z.array(z.object({ code: z.string(), level: z.enum(['info', 'warning', 'critical']) })),
    }),
    /** anonymised operating counters for the owner portal (04 §3: no guest names ever) */
    summary: z
        .object({
        occupancyPct: z.number().nullable(),
        inHouse: z.number().int().nonnegative(),
        arrivalsToday: z.number().int().nonnegative(),
        departuresToday: z.number().int().nonnegative(),
        roomsInService: z.number().int().nonnegative(),
    })
        .nullable(),
    licenseId: uuidSchema.nullable(),
    commandsAckedUpTo: z.number().int().nonnegative(),
});
export const heartbeatAckSchema = z.object({
    serverTime: z.iso.datetime(),
    minSupportedNodeVersion: z.string(),
    commandsPending: z.number().int().nonnegative(),
    nextHeartbeatSeconds: z.number().int().positive(),
});
export const commandAckSchema = z.object({
    results: z
        .array(z.object({
        commandId: uuidSchema,
        sequence: z.number().int().nonnegative(),
        result: z.enum(['applied', 'rejected', 'ignored']),
        error: z.string().max(500).optional(),
    }))
        .max(200),
});
/** Command types the cloud emits today (05 §3.3); the node ignores unknown types and reports `ignored`. */
export const COMMAND_TYPES = [
    'LicenseIssued',
    'LicenseRevoked',
    'FeatureFlagsUpdated',
    'MaintenanceWindowSet',
    'SupportAccessRequested',
    'SupportAccessClosed',
    'UserProvisioned',
    'UserDisabled',
    // ADR-0022 channel layer
    'ChannelBookingCreated',
    'ChannelBookingCancelled',
    'ChannelRequestDecided',
];
//# sourceMappingURL=cloud.js.map